import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthWrapperComponent } from "../auth-wrapper/auth-wrapper.component";
import { FormInputComponent } from "../../utils/form-input/form-input.component";
import { MdbButtonComponent } from "../../utils/mdb-button/mdb-button.component";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { environment } from '../../../../environments/environment';
import { MdbValidationModule } from "mdb-angular-ui-kit/validation";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../services/auth/auth.service";
import { finalize } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { HttpErrorsService } from "../../../services/utils/http-errors.service";
import { CustomNotificationService } from "../../../services/utils/custom-notification.service";

@Component({
    selector: 'app-reset-password',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    AuthWrapperComponent,
    FormInputComponent,
    MdbButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    MdbValidationModule
],
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent {
  token: string | null = null
  loading = signal(false)
  formGroup: FormGroup = this.formBuilder.group(
    {
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, Validators.minLength(environment.passwordMinLength)]],
      confirmPassword: [null, [Validators.required]]
    },
    {
      validators: this.matchValidator('password', 'confirmPassword')
    }
  )

  private readonly destroyRef = inject(DestroyRef);

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private authService: AuthService,
              private httpErrorService: HttpErrorsService,
              private notificationService: CustomNotificationService,
              private cdr: ChangeDetectorRef,
              private ngZone: NgZone) {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.token = params.get('token');
        const email = params.get('email');
        if (email) {
          this.formGroup.patchValue({ email });
        }
      });
  }

  getFormControl(name: string): AbstractControl | null {
    return this.formGroup.get(name)
  }

  submit() {
    this.formGroup.markAllAsTouched()

    if (this.formGroup.invalid)
      return

    if (!this.token) {
      this.getFormControl('email')?.setErrors({ invalidToken: true })
      return
    }

    this.loading.set(true)
    this.authService.resetPassword({
      token: this.token,
      email: this.getFormControl('email')?.value,
      password: this.getFormControl('password')?.value,
      password_confirmation: this.getFormControl('confirmPassword')?.value,
    })
      .pipe(
        finalize(() => {
          this.ngZone.run(() => {
            this.loading.set(false)
            this.cdr.detectChanges()
          })
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.notificationService.success('AUTH.PASSWORD_RESET_SUCCESS')
          this.router.navigate(['/auth/login'])
        },
        error: (e: HttpErrorResponse) => {
          this.httpErrorService.handleError(e)
        }
      })
  }

  matchValidator(controlName: string, matchingControlName: string): ValidatorFn {
    return (abstractControl: AbstractControl) => {
      const control = abstractControl.get(controlName);
      const matchingControl = abstractControl.get(matchingControlName);

      if (matchingControl!.errors && !matchingControl!.errors?.['confirmedValidator']) {
        return null;
      }

      if (control!.value !== matchingControl!.value) {
        const error = {confirmedValidator: 'Passwords do not match.'};
        matchingControl!.setErrors(error);
        return error;
      } else {
        matchingControl!.setErrors(null);
        return null;
      }
    }
  }

  protected readonly environment = environment;
}
